import { useState } from "react";

import type { OpenAPI } from "simplydo/interfaces";
import { Loader } from "simplydo/react/ui/loader";
import { Tab } from "simplydo/react/ui/tab";

import { ImpactStatementForm } from "./ImpactStatementForm";
import { ImpactStatementReadonly } from "./ImpactStatementReadonly";
import { impactStageDefinitions, impactStageOrder } from "./statementUtils";
import { useImpactStatements } from "./useImpactStatements";

type ImpactStatementProps = {
  ideaId: string;
  forStage?: OpenAPI.Schemas["ImpactStatement"]["stage"];
  restrictStagesTo?: OpenAPI.Schemas["ImpactStatement"]["stage"][];
  editable?: boolean;
};

const StageContent = ({
  ideaId,
  stage,
  editable,
}: {
  ideaId: string;
  stage: OpenAPI.Schemas["ImpactStatement"]["stage"];
  editable?: boolean;
}) => {
  const rangeInputType = impactStageDefinitions[stage].rangeInputType;

  if (editable) {
    return (
      <ImpactStatementForm
        ideaId={ideaId}
        stage={stage}
        rangeInputType={rangeInputType}
        showPreviousStatement
      />
    );
  }

  return (
    <ImpactStatementReadonly
      ideaId={ideaId}
      stage={stage}
      rangeInputType={rangeInputType}
    />
  );
};

export const ImpactStatementEditor = ({
  ideaId,
  forStage,
  restrictStagesTo: restrictStagesToProp,
  editable,
}: ImpactStatementProps) => {
  const {
    impactStatements,
    statementDefinition,
    isLoading,
    canManageIdea,
    restrictStagesTo: defaultRestrictStagesTo,
  } = useImpactStatements(ideaId);

  const availableStages = restrictStagesToProp ?? defaultRestrictStagesTo;
  const existingStages = Object.keys(
    impactStatements,
  ) as OpenAPI.Schemas["ImpactStatement"]["stage"][];
  const [stage, setStage] = useState(forStage || existingStages[0]);

  if (isLoading) {
    return <Loader />;
  }

  if (!statementDefinition) {
    return null;
  }

  if (availableStages && availableStages.length === 1) {
    const onlyStage = availableStages[0]!;

    return (
      <StageContent ideaId={ideaId} stage={onlyStage} editable={editable} />
    );
  }

  return (
    <Tab
      activeIndex={impactStageOrder.indexOf(stage!)}
      onTabChange={(_, data) => {
        const newStage = impactStageOrder[data.activeIndex as number];
        setStage(newStage);
      }}
      variant="underline"
      panes={impactStageOrder
        .filter((s) => !availableStages || availableStages.includes(s))
        .map((s) => ({
          menuItem: impactStageDefinitions[s].name,
          render: () => {
            const canEditStage =
              editable || (canManageIdea && s !== "userEstimate");

            return (
              <StageContent ideaId={ideaId} stage={s} editable={canEditStage} />
            );
          },
        }))}
    />
  );
};
